import { useState } from "react";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: "",
    website_url: "" // Honeypot antispam field
  });

  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Antispam bot check
    if (formData.website_url) {
      // Bot filled out the honeypot
      setStatus("success");
      return;
    }

    if (!formData.name.trim() || !formData.email.trim() || !formData.description.trim()) {
      setErrorMessage("Please complete all required fields.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch("/_api/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          _to: "prajwal@prajwalhalwai.com",
          recipient: "prajwal@prajwalhalwai.com",
          email_to: "prajwal@prajwalhalwai.com",
          reply_to: formData.email,
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: formData.description,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "",
          phone: "",
          email: "",
          description: "",
          website_url: ""
        });
      } else {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(data.message || "Failed to submit enquiry. Please email prajwal@prajwalhalwai.com directly.");
        setStatus("error");
      }
    } catch {
      setErrorMessage("Network connection error. Please try again or reach out at prajwal@prajwalhalwai.com.");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-2xl bg-[#12151D] border border-[#262D3D] p-8 sm:p-10 text-center space-y-4 shadow-xl">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#CC3737]/15 border border-[#CC3737]/30 flex items-center justify-center text-[#CC3737] text-2xl">
          <i className="fa-solid fa-check" />
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
          Thank you for reaching out!
        </h3>
        <p className="text-sm sm:text-base text-[#94A3B8] max-w-md mx-auto">
          I have received your project enquiry. I will review what you have shared and get back to you within 1-2 business days.
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-4 px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#1A1F2B] hover:bg-[#232938] border border-[#2E3649] transition-colors cursor-pointer"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl bg-[#10131A] border border-[#1F2533] p-6 sm:p-10 space-y-6 shadow-xl">
      {/* Antispam honeypot field - strictly hidden from human view */}
      <div className="sr-only absolute -left-[9999px] top-0" aria-hidden="true">
        <label htmlFor="website_url">Leave this field empty</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      <div className="space-y-1">
        <h3 className="text-xl font-bold text-white tracking-tight">
          Tell me about your idea
        </h3>
        <p className="text-xs sm:text-sm text-[#94A3B8]">
          Share what you are building. You don't need a full technical spec to start.
        </p>
      </div>

      {status === "error" && (
        <div className="p-4 rounded-xl bg-red-950/40 border border-red-800/60 text-red-300 text-xs sm:text-sm flex items-start gap-3">
          <i className="fa-solid fa-circle-exclamation mt-0.5 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {/* Name */}
        <div className="space-y-1.5">
          <label htmlFor="name" className="block text-xs font-semibold text-[#CBD5E1] uppercase tracking-wider">
            Your Name <span className="text-[#CC3737]">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            placeholder="e.g. Alex Jensen"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-[#161A24] border border-[#282F3E] text-white placeholder-[#64748B] text-sm focus:outline-none focus:border-[#CC3737] focus:ring-1 focus:ring-[#CC3737] transition-colors"
          />
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <label htmlFor="phone" className="block text-xs font-semibold text-[#CBD5E1] uppercase tracking-wider">
            Phone Number <span className="text-[#CC3737]">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            placeholder="e.g. +45 50 38 21 38"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl bg-[#161A24] border border-[#282F3E] text-white placeholder-[#64748B] text-sm focus:outline-none focus:border-[#CC3737] focus:ring-1 focus:ring-[#CC3737] transition-colors"
          />
        </div>
      </div>

      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="email" className="block text-xs font-semibold text-[#CBD5E1] uppercase tracking-wider">
          Email Address <span className="text-[#CC3737]">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          placeholder="e.g. alex@example.com"
          value={formData.email}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-xl bg-[#161A24] border border-[#282F3E] text-white placeholder-[#64748B] text-sm focus:outline-none focus:border-[#CC3737] focus:ring-1 focus:ring-[#CC3737] transition-colors"
        />
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <div className="flex justify-between items-baseline">
          <label htmlFor="description" className="block text-xs font-semibold text-[#CBD5E1] uppercase tracking-wider">
            Project / Idea Description <span className="text-[#CC3737]">*</span>
          </label>
          <span className="text-[11px] text-[#64748B]">Brief or detailed</span>
        </div>
        <textarea
          id="description"
          name="description"
          required
          rows={5}
          placeholder="Briefly explain:&#10;• What is the idea or problem you want to solve?&#10;• What do you currently have (sketches, designs, existing code)?&#10;• What do you need help with (MVP, full SaaS, architecture)?"
          value={formData.description}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-xl bg-[#161A24] border border-[#282F3E] text-white placeholder-[#64748B] text-sm leading-relaxed focus:outline-none focus:border-[#CC3737] focus:ring-1 focus:ring-[#CC3737] transition-colors resize-y"
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full py-3.5 px-6 rounded-xl font-semibold text-sm text-white bg-[#CC3737] hover:bg-[#b52f2f] active:bg-[#9e2727] disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-md shadow-[#CC3737]/20 flex items-center justify-center gap-2 cursor-pointer"
      >
        {status === "loading" ? (
          <>
            <i className="fa-solid fa-spinner-third fa-spin text-sm" />
            <span>Sending enquiry...</span>
          </>
        ) : (
          <>
            <span>Discuss your idea</span>
            <i className="fa-solid fa-arrow-right text-xs" />
          </>
        )}
      </button>

      <p className="text-[11px] text-[#64748B] text-center">
        No sales reps. Direct, confidential conversation with Prajwal Halwai.
      </p>
    </form>
  );
}
